import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import GuestHeader from '@/components/layouts/GuestHeader';
import StaticFooter from '@/components/static/StaticFooter';
import { client } from '@/services/apiClient';
import { fetchNewsFeed } from '@/services/news';
import { useCurrentUser } from '@/services/userHooks';
import { useToast } from '@/hooks/useToast';
import { formatRelativeTime } from '@/utils/dateUtils';
import type { NewsItem } from '@/types/api';
import '@/styles/static.css';

const SIGN_UP_URL = '/user/register?next=/news';

interface NewsThumbnailProps {
  item: NewsItem;
}

function NewsThumbnail({ item }: NewsThumbnailProps) {
  if (!item.og_image) {
    // Neutral placeholder keeps rows aligned when an article has no image.
    return (
      <div
        aria-hidden="true"
        className="flex h-16 w-24 shrink-0 items-center justify-center rounded bg-gray-100 text-lg font-semibold text-gray-300"
      >
        {(item.host[0] || '').toUpperCase()}
      </div>
    );
  }
  return (
    <img
      src={item.og_image}
      alt=""
      loading="lazy"
      className="h-16 w-24 shrink-0 rounded bg-gray-100 object-cover"
    />
  );
}

interface SaveButtonProps {
  item: NewsItem;
  isLoggedIn: boolean;
  isSaved: boolean;
  isSaving: boolean;
  onSave: (item: NewsItem) => void;
}

function SaveButton({ item, isLoggedIn, isSaved, isSaving, onSave }: SaveButtonProps) {
  if (isSaved) {
    return (
      <span className="shrink-0 self-center rounded-full border border-gray-200 px-3 py-1 text-sm font-medium text-gray-400">
        <Trans>Saved</Trans>
      </span>
    );
  }
  if (!isLoggedIn) {
    // Saving requires an account: send visitors to sign-up, returning here.
    return (
      <a
        href={SIGN_UP_URL}
        className="shrink-0 self-center rounded-full border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:border-gray-400 hover:text-gray-900"
      >
        <Trans>Save</Trans>
      </a>
    );
  }
  return (
    <button
      type="button"
      disabled={isSaving}
      onClick={() => onSave(item)}
      className="shrink-0 self-center rounded-full border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:border-gray-400 hover:text-gray-900 disabled:cursor-default disabled:opacity-60"
    >
      {isSaving ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
    </button>
  );
}

interface NewsListItemProps {
  item: NewsItem;
  isLoggedIn: boolean;
  isSaved: boolean;
  isSaving: boolean;
  onSave: (item: NewsItem) => void;
}

function NewsListItem({
  item,
  isLoggedIn,
  isSaved,
  isSaving,
  onSave,
}: NewsListItemProps) {
  return (
    <li className="flex gap-4 border-b border-gray-100 py-4 last:border-b-0">
      <span className="w-7 shrink-0 pt-1 text-right text-sm text-gray-400 tabular-nums">
        {item.rank}
      </span>
      <NewsThumbnail item={item} />
      <div className="min-w-0 flex-1">
        <a
          href={item.url}
          target="_blank"
          rel="nofollow ugc noopener"
          className="text-lg leading-snug font-medium text-gray-900 hover:underline"
        >
          {item.title || item.url}
        </a>
        <div className="mt-1 text-sm text-gray-500">
          <span>{item.host}</span>
          <span className="mx-1.5" aria-hidden="true">
            ·
          </span>
          <span>{formatRelativeTime(item.first_seen)}</span>
        </div>
      </div>
      <SaveButton
        item={item}
        isLoggedIn={isLoggedIn}
        isSaved={isSaved}
        isSaving={isSaving}
        onSave={onSave}
      />
    </li>
  );
}

function NewsPage() {
  const { currentUser } = useCurrentUser();
  const { showToast } = useToast();
  const [savedUrls, setSavedUrls] = useState<Set<string>>(new Set());
  const [savingUrl, setSavingUrl] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['newsFeed'],
    queryFn: () => fetchNewsFeed(),
  });

  const saveMutation = useMutation({
    mutationFn: (item: NewsItem) => client.addBookmark(item.url),
    onMutate: (item: NewsItem) => {
      setSavingUrl(item.url);
    },
    onSuccess: (_bookmark, item) => {
      setSavedUrls((previous) => new Set(previous).add(item.url));
      showToast(t`Saved to Instapaper`, 'success');
    },
    onError: () => {
      showToast(t`Could not save the article. Please try again.`, 'error');
    },
    onSettled: () => {
      setSavingUrl(null);
    },
  });

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <GuestHeader />
      <div className="static-page flex flex-1 flex-col">
        <div className="mx-auto w-full max-w-2xl px-5 py-10">
          <h1 className="text-3xl font-semibold text-gray-900">
            <Trans>News</Trans>
          </h1>
          <p className="mt-2 text-gray-500">
            <Trans>What Instapaper readers are saving and reading right now.</Trans>
          </p>

          {isLoading && (
            <p className="mt-10 text-gray-400">
              <Trans>Loading…</Trans>
            </p>
          )}
          {isError && (
            <p className="mt-10 text-gray-400">
              <Trans>
                The news feed is unavailable right now. Try again in a few minutes.
              </Trans>
            </p>
          )}
          {data && data.items.length === 0 && (
            <p className="mt-10 text-gray-400">
              <Trans>Nothing here yet — check back soon.</Trans>
            </p>
          )}
          {data && data.items.length > 0 && (
            <ol className="mt-6 list-none p-0">
              {data.items.map((item) => (
                <NewsListItem
                  key={item.url}
                  item={item}
                  isLoggedIn={Boolean(currentUser)}
                  isSaved={Boolean(item.saved) || savedUrls.has(item.url)}
                  isSaving={savingUrl === item.url}
                  onSave={(target) => saveMutation.mutate(target)}
                />
              ))}
            </ol>
          )}
        </div>
        {/* Must live inside .static-page: all footer styles are scoped
            .static-page .static-footer, and margin-top:auto pins it to the
            bottom of this flex column on short pages. */}
        <StaticFooter />
      </div>
    </div>
  );
}

export default NewsPage;
